import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Request from "../../core/requests";
import { Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import MetricCard from "components/cards/metrics/MetricCard.component";
import { getTimeZoneStandard } from "utils/timezone";
import {
  propsBarPressCard,
  propsCo2Card,
  propsLuxCard,
  propsPCountCard,
  propsPM10Card,
  propsPM2_5Card,
  propsRainCard,
  propsRainPeakCard,
  propsTempertureCard,
  propsUmidityCard,
  propsWindCard,
  propsWindDirCard,
} from "constants/metrics";
import { Loading } from "components/common";
import { refreshToken } from "store/user.store";
import { logout } from "store/user.store";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { IconInfoCircle } from "@tabler/icons";
import { WIND_DIRECTION_DICT } from "constants/wind";

const CurrentMetricDataContainer = ({
  zoneSelected,
  stationIdSelected,
  onFailure = () => { },
  metric,
  refresh,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { token, user, refresh_token } = useSelector((state) => state.user);

  const [dataCurrent, setDataCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstRefresh, setIsFirstRefresh] = useState(true);

  const cardsValues = useMemo(
    () => ({
      air_temp: {
        label: "temperature",
        props: propsTempertureCard,
      },
      air_hum: {
        label: "humidity",
        props: propsUmidityCard,
      },
      CO2: {
        label: "CO2",
        props: propsCo2Card,
      },
      lux: {
        label: "illuminance",
        props: propsLuxCard,
      },
      bar_press: {
        label: "atmospheric-pressure",
        props: propsBarPressCard,
      },
      wind_speed: {
        label: "wind-speed",
        props: propsWindCard,
      },
      wind_dir_idx: {
        label: "wind-direction",
        props: propsWindDirCard,
      },
      rain_intensity: {
        label: 'Rainfall intensity',
        props: propsRainCard,
      },
      rain_intensity_peak: {
        label: "precipitation-peak",
        props: propsRainPeakCard,
      },
      pcount: {
        label: "people-counter",
        props: propsPCountCard,
      },
      pm10: {
        label: "PM 10",
        props: propsPM10Card,
      },
      pm2_5: {
        label: "PM 2.5",
        props: propsPM2_5Card,
      },
    }),
    []
  );

  const onError = useCallback(
    (error) => {
      if (isFirstRefresh) {
        setLoading(false);
        if (error?.response?.status === 403) {
          onLogout(error);
        } else {
          if (error?.response?.status !== 401) {
            onFailure(error);
          } else {
            if (isFirstRefresh) {
              dispatch(
                refreshToken({
                  grant_type: "refresh_token",
                  refresh_token: user.refresh_token,
                })
              );
              setIsFirstRefresh(false);
            }
          }
        }
      } else {
        onLogout(error);
      }
    },
    // eslint-disable-next-line
    []
  );
  const onLogout = useCallback((error) => {
    if (error?.response?.status === 403 || error?.response?.status === 401) {
      dispatch(logout());
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setLoading(true);
    if (stationIdSelected) {
      Request.stations
        .getDataCurrentStationId({
          station_id: stationIdSelected,
          token: token,
        })
        .then((resp) => {
          setDataCurrent(resp);
          setLoading(false);
        })
        .catch((error) => {
          onError(error);
        });
    } else {
      const path =
        zoneSelected && !_.isEmpty(zoneSelected)
          ? `?cluster=${zoneSelected}`
          : ``;
      Request.stations
        .getDataCurrentGeneric({ path: path, token: token })
        .then((resp) => {
          setDataCurrent(resp);
          setLoading(false);
          setIsFirstRefresh(true);
        })
        .catch((error) => onError(error));
    }
    // eslint-disable-next-line
  }, [zoneSelected, stationIdSelected, refresh, token, refresh_token]);

  return loading ? (
    <Loading />
  ) : (
    <>
      {dataCurrent && (
        <Grid container spacing={1}>
          {metric &&
            metric.map((item, index) => (
              <Fragment key={index}>
                {index === 0 && (
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Typography
                      align="center"
                      variant="h5"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "white"
                            : theme.palette.primary.main,
                      }}
                    >
                      {t("Real-time data")}
                    </Typography>
                    <Tooltip
                      title={
                        dataCurrent?.time ?
                          `${t("Updated to")}: ${DateTime.fromISO(
                            getTimeZoneStandard(dataCurrent?.time)
                          ).toLocaleString(DateTime.DATETIME_MED, {
                            locale: i18n?.language ? i18n.language : "it",
                          })}` : `${t("Updated to")}: ${t('unknown')}`
                      }
                    >
                      <IconButton
                        sx={{
                          p: 0,
                          pl: 0.5,
                          color:
                            theme.palette.mode === "dark"
                              ? "white"
                              : theme.palette.primary.main,
                        }}
                      >
                        <IconInfoCircle style={{ width: 13, height: 13 }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}

                <Grid
                  item
                  key={index}
                  xs={12}
                  sm={metric.length > 1 ? 6 : 12}
                  md={metric.length > 1 ? 6 : 12}
                  lg={metric.length > 1 ? 6 : 12}
                >
                  <MetricCard
                    label={t(cardsValues[item.endpoint].label)}
                    isTrend={true}
                    {...cardsValues[item.endpoint].props}
                    symbol={
                      item.endpoint === "wind_speed" &&
                        (dataCurrent["wind_speed"]?.data === null ||
                          dataCurrent["wind_speed"]?.data === 0)
                        ? ""
                        : t(cardsValues[item.endpoint].props.symbol)
                    }
                    data={
                      item.endpoint === "wind_dir_idx"
                        ? dataCurrent["wind_dir_idx"]?.data === null ||
                          dataCurrent["wind_speed"]?.data === null ||
                          dataCurrent["wind_speed"]?.data === 0
                          ? { data: null }
                          : {
                            data: t(
                              WIND_DIRECTION_DICT[
                              dataCurrent[item.endpoint]?.data
                              ]
                            ),
                            trend: dataCurrent[item.endpoint]?.trend,
                          }
                        : item.endpoint === "wind_speed" &&
                          (dataCurrent[item.endpoint]?.data === 0 ||
                            dataCurrent[item.endpoint]?.data === null)
                          ? { data: null }
                          : dataCurrent[item.endpoint]
                    }
                    loading={loading}
                  />
                </Grid>
              </Fragment>
            ))}
        </Grid>
      )}
    </>
  );
};

export default CurrentMetricDataContainer;
